// src/app/core/services/app-update.service.ts
import { Injectable, OnDestroy, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, Subject, filter, interval, takeUntil } from 'rxjs';

/**
 * Servicio centralizado para detectar y aplicar actualizaciones del SW.
 *
 * Estrategia robusta:
 *  1. Escucha `versionUpdates` (API moderna Angular 13+) para detectar nueva versión lista.
 *  2. Llama `checkForUpdate()` de forma proactiva cada CHECK_INTERVAL_MS para no
 *     depender únicamente del evento pasivo del SW (que en algunas máquinas tarda o falla).
 *  3. Expone `updateAvailable$` para que cualquier componente pueda reaccionar.
 *  4. `applyUpdate()` activa la nueva versión con `activateUpdate()` antes del reload,
 *     garantizando que el SW nuevo quede en control inmediatamente.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
    private readonly swUpdate = inject(SwUpdate);
    private readonly destroy$ = new Subject<void>();

    /** Emite `true` cuando hay una versión nueva lista para instalar. */
    readonly updateAvailable$ = new BehaviorSubject<boolean>(false);

    /** Cada cuánto se consulta activamente al SW (5 minutos). */
    private readonly CHECK_INTERVAL_MS = 5 * 60 * 1000;

    constructor() {
        if (!this.swUpdate.isEnabled) {
            // En dev mode el SW no está habilitado; no hacer nada.
            return;
        }

        this.listenForUpdates();
        this.schedulePeriodicChecks();
        // Verificación inmediata al arrancar la app
        this.checkNow();
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Privados
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Escucha el stream `versionUpdates` filtrado a `VERSION_READY`.
     * Este evento garantiza que el nuevo SW ya descargó todos sus assets.
     */
    private listenForUpdates(): void {
        this.swUpdate.versionUpdates
            .pipe(
                filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                console.info('[AppUpdateService] Nueva versión disponible.');
                this.updateAvailable$.next(true);
            });
    }

    /**
     * Polling proactivo: llama `checkForUpdate()` periódicamente.
     * Resuelve el caso donde el SW no dispara el evento pasivo en algunas máquinas.
     */
    private schedulePeriodicChecks(): void {
        interval(this.CHECK_INTERVAL_MS)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.checkNow());
    }

    private async checkNow(): Promise<void> {
        try {
            await this.swUpdate.checkForUpdate();
        } catch (err) {
            // No propagar errores silenciosos del SW
            console.warn('[AppUpdateService] checkForUpdate falló:', err);
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Público
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Activa el nuevo SW y recarga la página.
     * Llama a `activateUpdate()` primero para evitar que el SW viejo
     * sirva el `index.html` después del reload.
     */
    async applyUpdate(): Promise<void> {
        try {
            await this.swUpdate.activateUpdate();
        } catch (err) {
            console.warn('[AppUpdateService] activateUpdate falló, recargando igual:', err);
        } finally {
            // Hard reload: limpia completamente la caché del navegador
            window.location.reload();
        }
    }

    /** Permite descartar el banner sin actualizar. */
    dismissUpdate(): void {
        this.updateAvailable$.next(false);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
